<template>
	<v-dialog v-model="createDialog" persistent max-width="500px">
		<v-card class="new-back lighten2">
			<v-card-title>
				<span class="headline">Create API Token</span>
			</v-card-title>
			<v-card-text>
				<v-form v-if="!creationResponse" ref="form" v-model="valid">
					<v-container>
						<v-row no-gutters>
							<v-col cols="12">
								<label>Name *</label>
								<v-text-field v-model="newToken.name" :rules="rules.required" required outlined dense/>
							</v-col>
							<v-col cols="12">
								<label>Description</label>
								<v-textarea v-model="newToken.description" rows="2" outlined hide-details dense/>
							</v-col>
							<v-col cols="12">
								<div class="d-flex align-center gap-4">
									<label>Expiration:</label>
									<v-switch v-model="expiration" :label="expiration ? 'Yes' : 'No'" />
								</div>
								<q-date-picker
									v-if="expiration" v-model="newToken.expirationDateTime" :min="today"
									label="Expiration Date" outlined inner dense
								/>
							</v-col>
							<v-col cols="12">
								<h3 class="mb-3">Permissions</h3>
								<v-expansion-panels v-model="expandedPanel" focusable accordion flat>
									<v-expansion-panel
										v-for="(permission) in permissions.filter(x => !x.deprecated)"
										:key="permission.id || permission.value" :readonly="!permission.children"
										class="bordered darken new-back"
									>
										<v-expansion-panel-header class="panel-header">
											<div>
												{{ permission.name }}
											</div>
											<template v-slot:actions>
												<div v-if="!permission.children">
													<access-level-select :value="getPermissionByValue(permission.value).accessLevel"
														:type="permission.levelTypes" @input="(v) => {
															if (permission) {
																let pem = getPermissionByValue(permission.value)
																pem.accessLevel = v
															}
														}" />
												</div>
											</template>
										</v-expansion-panel-header>
										<v-expansion-panel-content class="panel-content" v-if="permission.children">
											<div class="line" v-for="(subPermission, ix) in permission.children.filter(x => !x.deprecated)"
												:key="ix">
												<div class="line-title">{{ subPermission.name }}</div>
												<div class="content">
													<access-level-select :value="getPermissionByValue(subPermission.value).accessLevel"
														:type="subPermission.levelTypes" @input="(v) => {
															let pem = getPermissionByValue(subPermission.value)
															pem.accessLevel = v
														}" />
												</div>
											</div>
										</v-expansion-panel-content>
									</v-expansion-panel>
								</v-expansion-panels>
							</v-col>
						</v-row>
					</v-container>
				</v-form>
				<div v-else>
					<v-card elevation="0" outlined>
						<v-card-text class="d-flex flex-column gap-6 high-back">
							<v-alert text type="success" class="mb-0" dense>
								API Token Created
							</v-alert>
							<div>
								<strong>Name</strong>
								<v-text-field
									:value="creationResponse.name"
									readonly filled dense hide-details
								/>
							</div>
							<div>
								<strong>Preview</strong>
								<div class="d-flex align-center gap-2">
									<v-text-field
										@click:append="showFullToken = !showFullToken"
										:append-icon="showFullToken ? 'mdi-eye' : 'mdi-eye-off'"
										:value="showFullToken ? creationResponse.token : '........................' + creationResponse.preview"
										readonly filled dense hide-details
									/>
									<v-btn @click="copyToken" icon>
										<v-icon>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div>
								<strong>Expiration Date</strong>
								<div v-if="creationResponse.expirationDateTime" class="mt-1">{{ formatDate(creationResponse.expirationDateTime) }}</div>
								<div v-else class="mt-1">Never</div>
							</div>
							<div class="mt-2">
								<v-alert text type="warning" class="mb-0" dense>
									You will not be able to see the full token after this dialog is closed.
								</v-alert>
							</div>
						</v-card-text>
					</v-card>
				</div>
			</v-card-text>
			<v-card-actions>
				<v-spacer></v-spacer>
				<template v-if="!creationResponse">
					<v-btn color="primary" text @click="closeCreateDialog">Cancel</v-btn>
					<v-btn color="primary" @click="saveToken" :disabled="!valid">Create</v-btn>
				</template>
				<template v-else>
					<v-btn color="primary" text @click="closeCreateDialog">Close</v-btn>
				</template>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import AccessLevelSelect from '../AccessLevelSelect'
import moment from 'moment'

export default {
	components: {
		AccessLevelSelect
	},
	props: {
		permissions: {
			type: Array,
			required: true
		}
	},
	data:() => ({
		createDialog: false,
		valid: false,
		rules: {
			required: [
				v => !!v || 'Field is required'
			]
		},
		newToken: {
			name: '',
			description: '',
			permissions: [],
			expirationDateTime: null
		},
		expiration: false,
		today: moment().format('YYYY-MM-DD'),
		expandedPanel: null,
		creationResponse: null,
		showFullToken: false,
	}),
	methods: {
		openCreateDialog() {
			this.createDialog = true;
		},
		closeCreateDialog() {
			this.createDialog = false;
			this.creationResponse = null;
			this.showFullToken = false;
			this.newToken = {
				name: '',
				description: '',
				permissions: [],
				expirationDateTime: null
			};
			this.expiration = false;
		},
    getPermissionByValue(value) {
      if (!this.newToken.permissions) {
        this.newToken.permissions = [];
      }
      let perm = this.newToken.permissions.find(p => p.type == value);
      if (!perm) {
        perm = { type: value, accessLevel: 0 };
        this.newToken.permissions.push(perm);
      }
      return perm;
    },
    saveToken() {
      if (!this.$refs.form.validate()) {
        window.getApp.error('Please fill all required fields');
        return;
      }

      const permissionsToSend = this.newToken.permissions.filter(p => p.accessLevel !== 0);

      const tokenData = {
        name: this.newToken.name,
        description: this.newToken.description,
				expirationDateTime: this.newToken.expirationDateTime,
        permissions: permissionsToSend
      };

      this.$q.save({
        api: '/api/apiToken',
        data: tokenData,
        successMsg: "API token created successfully.",
        afterSuccessUrl: null,
        errorMsg: "Cannot create API token.",
        afterErrorUrl: null,
      }).then(res => {
        this.$emit('save');
        this.creationResponse = res.data;
        this.showFullToken = false;
      });
    },
    copyToken() {
      const tokenToCopy = this.creationResponse.token;
      navigator.clipboard.writeText(tokenToCopy).then(() => {
        window.getApp.success('Token copied to clipboard');
      }).catch(() => {
        window.getApp.error('Failed to copy token');
      });
    },
	}
}

</script>